// ============================================================================
// OBT F3-E — Kullanıcı bildirim yüzeyi (liste + okundu + derin-bağlantı)
// ============================================================================
// Salt-okuma + okundu işaretleme. Derin-bağlantı YALNIZ rolün sahip olduğu OBT
// görünümüne gider; hedefi olmayan olay yalnız okundu işaretlenir (yetkisiz
// ekran AÇILMAZ). Backend her açılışta ACL'i yeniden uygular.
import { ArrowLeft, Bell, Check, CheckCheck, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ObtPageHeader, ObtLoading, ObtErrorState, ObtEmptyState } from '../ObtStates';
import { useObtNotifications } from './hooks/useObtNotifications';
import { notificationLabel, notificationTargetView } from './notificationEnums';
import { formatDate } from '../report/reportFormat';

/**
 * @param {object} props
 * @param {'director'|'deputy'|'engineer'|'foreman'} props.role
 * @param {boolean} [props.enabled]
 * @param {(view: string, notification: object) => void} [props.onNavigate]
 * @param {() => void} [props.onBack]
 */
export default function ObtNotificationsPanel({ role = 'director', enabled = true, onNavigate, onBack }) {
  const { loading, error, items, unreadCount, reload, markRead, markAll } = useObtNotifications({ enabled });

  const openTarget = (n) => {
    const view = notificationTargetView(n.event_type, role);
    markRead(n.notification_id);
    if (view && onNavigate) onNavigate(view, n);
  };

  return (
    <div className="space-y-4">
      <ObtPageHeader
        onBack={onBack}
        title="Bildirimler"
        description="OBT bilgi talebi, inceleme, karar ve kurumsal hafıza bildirimleriniz."
        actions={
          unreadCount > 0 ? (
            <Button variant="outline" size="sm" onClick={markAll}>
              <CheckCheck className="mr-2 h-4 w-4" aria-hidden="true" /> Tümünü okundu işaretle
            </Button>
          ) : null
        }
      />

      <p className="text-sm text-gray-500" aria-live="polite">
        {unreadCount > 0 ? `${unreadCount} okunmamış bildirim` : 'Okunmamış bildirim yok'}
      </p>

      {loading && <ObtLoading label="Bildirimler yükleniyor…" />}
      {!loading && error && <ObtErrorState error={error} onRetry={reload} />}
      {!loading && !error && items.length === 0 && (
        <ObtEmptyState title="Bildiriminiz yok" description="OBT olayları burada listelenecek." icon={Bell} />
      )}

      {!loading && !error && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((n) => {
            const target = notificationTargetView(n.event_type, role);
            return (
              <li key={String(n.notification_id)}>
                <Card className={n.is_read ? '' : 'border-blue-200 bg-blue-50/40'}>
                  <CardContent className="flex flex-col gap-2 p-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        {!n.is_read && (
                          <span className="inline-block h-2 w-2 rounded-full bg-blue-500" aria-label="Okunmadı" />
                        )}
                        <span className="text-sm font-semibold text-gray-900">
                          {notificationLabel(n.event_type)}
                        </span>
                        <span className="text-xs text-gray-400">{formatDate(n.created_at)}</span>
                      </div>
                      {n.body && <p className="text-sm text-gray-600">{n.body}</p>}
                    </div>
                    <div className="flex flex-shrink-0 gap-1.5">
                      {target && (
                        <Button size="sm" variant="outline" onClick={() => openTarget(n)}>
                          Git <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                      {!n.is_read && (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label="Okundu işaretle"
                          onClick={() => markRead(n.notification_id)}
                        >
                          <Check className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
